import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { promptModal } from '@/components/ui/modal'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { financeKeys } from './api'
import { toastError } from './ui'

/** İptal edilebilen finans kayıtları. Kayıtlar doğrudan iptal edilmez; talep onaydan geçer. */
export type VoidTarget = 'ledger' | 'payment' | 'cash_entry' | 'transfer' | 'obligation_item'
export type VoidStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'failed'
export type VoidLevel = 'approver' | 'owner'

export type VoidRequest = {
  id: string
  targetType: VoidTarget
  targetId: string
  amount: string
  targetPeriod: string
  summary: string
  reason: string
  status: VoidStatus
  level: VoidLevel
  escalationReasons: string[]
  requestedBy: string
  requestedByName: string
  decidedBy: string | null
  decidedByName: string
  decidedAt: string | null
  decisionNote: string
  createdAt: string
}

const voidKeys = {
  all: ['finance', 'void-requests'] as const,
  list: (status: string) => ['finance', 'void-requests', status] as const,
  settings: ['finance', 'approval-settings'] as const,
}

const TARGET_PATH: Record<VoidTarget, (id: string) => [method: 'DELETE' | 'POST', path: string]> = {
  ledger: (id) => ['DELETE', `/api/v1/finance/ledger/${id}`],
  payment: (id) => ['DELETE', `/api/v1/finance/payments/${id}`],
  cash_entry: (id) => ['DELETE', `/api/v1/finance/cash-entries/${id}`],
  transfer: (id) => ['DELETE', `/api/v1/finance/transfers/${id}`],
  obligation_item: (id) => ['POST', `/api/v1/finance/obligation-items/${id}/unpay`],
}

function mapVoid(row: Record<string, unknown>): VoidRequest {
  const s = (v: unknown) => (v == null ? '' : String(v))
  return {
    id: s(row.id),
    targetType: s(row.target_type) as VoidTarget,
    targetId: s(row.target_id),
    amount: Number.isFinite(Number(row.amount)) ? Number(row.amount).toFixed(2) : '0.00',
    targetPeriod: s(row.target_period),
    summary: s(row.summary),
    reason: s(row.reason),
    status: (s(row.status) || 'pending') as VoidStatus,
    level: row.approval_level === 'owner' ? 'owner' : 'approver',
    escalationReasons: Array.isArray(row.escalation_reasons) ? row.escalation_reasons.map(String) : [],
    requestedBy: s(row.requested_by),
    requestedByName: s(row.requested_by_name),
    decidedBy: row.decided_by ? s(row.decided_by) : null,
    decidedByName: s(row.decided_by_name),
    decidedAt: row.decided_at ? s(row.decided_at) : null,
    decisionNote: s(row.decision_note),
    createdAt: s(row.created_at),
  }
}

function useVoidMut<A>(fn: (arg: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: voidKeys.all }), qc.invalidateQueries({ queryKey: financeKeys.all })]),
  })
}

export const useVoidRequests = (status: '' | VoidStatus) =>
  useQuery({
    queryKey: voidKeys.list(status),
    queryFn: async () => asList(await api.get<Record<string, unknown>[]>('/api/v1/finance/void-requests', status ? { status } : undefined)).map(mapVoid),
  })

export const useApproveVoid = () => useVoidMut(({ id, note }: { id: string; note?: string }) => api.post(`/api/v1/finance/void-requests/${id}/approve`, { note: note ?? '' }))
export const useRejectVoid = () => useVoidMut(({ id, note }: { id: string; note: string }) => api.post(`/api/v1/finance/void-requests/${id}/reject`, { note }))
export const useCancelVoid = () => useVoidMut((id: string) => api.post(`/api/v1/finance/void-requests/${id}/cancel`, {}))

export const useApprovalSettings = (enabled: boolean) =>
  useQuery({
    queryKey: voidKeys.settings,
    enabled,
    queryFn: async () => {
      const row = await api.get<Record<string, unknown>>('/api/v1/finance/approval-settings')
      return { ownerThreshold: Number(row.owner_threshold ?? 0).toFixed(2) }
    },
  })

export function useSaveApprovalSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ownerThreshold: string) => api.put('/api/v1/finance/approval-settings', { owner_threshold: ownerThreshold }),
    onSuccess: () => qc.invalidateQueries({ queryKey: voidKeys.settings }),
  })
}

/**
 * İptal talebi akışı: gerekçe sorulur, talep açılır ve kullanıcıya kimin onayına gittiği bildirilir.
 * Kayıt onaylanana kadar değişmez.
 */
export function useRequestVoid() {
  const { t } = useTranslation()
  const mut = useVoidMut(({ target, id, reason }: { target: VoidTarget; id: string; reason: string }) => {
    const [method, path] = TARGET_PATH[target](id)
    return method === 'DELETE' ? api.del<Record<string, unknown>>(path, { reason }) : api.post<Record<string, unknown>>(path, { reason })
  })
  const request = async (target: VoidTarget, id: string, description: string) => {
    const reason = await promptModal({
      title: t('finance.voids.requestTitle'),
      description: `${description} ${t('finance.voids.requestHint')}`,
      placeholder: t('finance.voids.reasonPlaceholder'),
      confirmLabel: t('finance.voids.requestConfirm'),
    })
    if (reason == null) return
    if (reason.trim().length < 5) {
      toast.error(t('finance.voids.reasonTooShort'))
      return
    }
    mut.mutate(
      { target, id, reason: reason.trim() },
      {
        onSuccess: (row) => {
          const level = (row as Record<string, unknown> | undefined)?.approval_level === 'owner' ? 'owner' : 'approver'
          toast.success(t(`finance.voids.requested.${level}`))
        },
        onError: toastError,
      },
    )
  }
  return { request, isPending: mut.isPending }
}
